import {createFileRoute, Outlet, redirect} from '@tanstack/react-router'
import {useSuspenseQuery} from '@tanstack/react-query'
import {ErrorBoundary} from 'react-error-boundary'
import {IntlProvider, IntlError} from 'use-intl'
import {lazy, Suspense} from 'react'
import getPreferredLocale, {AVAILABLE_LOCALES_MAP} from '~/client/fn/locale'
import {translationsQueryOptions} from '~/client/fn/translations'
import {shopQueryOptions} from '~/client/fn/shop'

const CookieBanner = lazy(() => import('~/components/cookie-banner/CookieBanner'))

const AVAILABLE_LANG_CODES = ['en', 'de']

export const Route = createFileRoute('/$lang')({
    component: RouteComponent,
    params: {
        parse: (params: {lang: string}): {lang: string; found?: boolean} => {
            if (AVAILABLE_LANG_CODES.includes(params.lang)) {
                return {lang: params.lang, found: true}
            }

            return {
                lang: '',
                found: false,
            }
        },
    },
    beforeLoad: async ({context, params, location}) => {
        const selectedLocale = params.found ? AVAILABLE_LOCALES_MAP[params.lang ?? ''] : undefined
        const locale = selectedLocale ?? (await getPreferredLocale())

        if (params.found === false) {
            throw redirect({
                // @ts-ignore
                to: `/$lang/${location.pathname}`,
                params: {
                    lang: locale.split('_')[0],
                },
                search: (prev) => prev,
            })
        }

        await context.queryClient.ensureQueryData(translationsQueryOptions(locale))
        return {locale}
    },
    loader: async ({context}) => {
        return await context.queryClient.ensureQueryData(shopQueryOptions())
    },
    head: ({matches, loaderData}) => {
        const currentMatch = matches[matches.length - 1]

        if (!currentMatch) {
            return {}
        }

        const currentPath = currentMatch.pathname
        const currentLang = currentMatch.params.lang

        const baseUrl = loaderData?.endpoint ?? ''
        const currentUrl = `${baseUrl}${currentPath}`

        return {
            links: [
                {
                    rel: 'canonical',
                    href: currentUrl,
                },
                ...AVAILABLE_LANG_CODES.filter((lang) => lang !== currentLang).map((lang) => ({
                    rel: 'alternate',
                    hrefLang: lang,
                    href: `${baseUrl}${currentPath.replace(/^\/[a-z]+/, `/${lang}`)}`,
                })),
                {
                    rel: 'alternate',
                    hrefLang: 'x-default',
                    href: `${baseUrl}${currentPath.replace(/^\/[a-z]+/, '')}`,
                },
            ],
        }
    },
})

const getMessageFallback = (info: {error: IntlError; key: string; namespace?: string}): string =>
    info.key

function RouteComponent() {
    const {lang} = Route.useParams()
    const {locale} = Route.useRouteContext()
    const {data: translations} = useSuspenseQuery(translationsQueryOptions(locale))

    return (
        <IntlProvider
            messages={translations}
            locale={lang || 'en'}
            timeZone="Europe/Berlin"
            getMessageFallback={getMessageFallback}
        >
            <Outlet />
            <ErrorBoundary fallback={null}>
                <Suspense>
                    <CookieBanner />
                </Suspense>
            </ErrorBoundary>
        </IntlProvider>
    )
}
